// BroadcastInvitesPanel.tsx
// -----------------------------------------------------------------------------
// TSX version with multi-status filtering and stable Accordion behavior.
// - AccordionSummary: only title + count (no interactive controls).
// - Filters moved into a toolbar row at the top of AccordionDetails.
// - Provider actions enabled only for PENDING (SENT/NO_RESPONSE).
// -----------------------------------------------------------------------------

import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Typography,
  Chip,
  Box,
  Button,
  Alert,
  Card,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { alpha } from "@mui/material/styles";
import { gql, useMutation, useQuery, ApolloError } from "@apollo/client";
import { Link as RouterLink, useLocation } from "react-router-dom";

/** =======================================================
 *  Types
 *  ===================================================== */
export type InviteStatus =
  | "DRAFT"
  | "SENT"
  | "ACCEPTED"
  | "DECLINED"
  | "EXPIRED"
  | "NO_RESPONSE";

type BusinessLite = { id: string; name?: string | null } | null;

export type ProviderInvite = {
  id: string;
  status: string; // normalized at read-time
  providerNote?: string | null;
  respondedAt?: string | null;
  business?: { id: string } | null;
  group?: { id: string; seedJson?: unknown; createdAt?: string | null } | null;
};

export type ReferrerGroupInvite = {
  id: string;
  status: string; // normalized at read-time
  respondedAt?: string | null;
  providerNote?: string | null;
  business?: BusinessLite;
  group?: {
    id: string;
    seedJson?: unknown;
    createdAt?: string | null;
    winnerInviteId?: string | null;
  } | null;
};

export type ReferrerGroup = {
  id: string;
  createdAt?: string | null;
  seedJson?: unknown;
  winnerInviteId?: string | null;
  invites: ReferrerGroupInvite[];
};

type RefGroupWorking = ReferrerGroup & { _rawInvites: ReferrerGroupInvite[] };

export type BroadcastInvitesPanelProps = {
  role: "provider" | "referrer";
  businessId?: number | null; // required for provider
  statuses?: InviteStatus[]; // optional initial override
  title?: string;
  panelMode?: boolean;
  onProviderInviteResponded?: () => void | Promise<void>;
  onCountChange?: (count: number) => void;
};

/** =======================================================
 *  GraphQL
 *  ===================================================== */
export const MY_PENDING_BROADCAST_INVITES = gql`
  query MyPendingBroadcastInvites($businessId: ID!, $statuses: [InviteStatus!]) {
    myReferralInvites(businessId: $businessId, statuses: $statuses) {
      id
      status
      providerNote
      respondedAt
      business {
        id
      }
      group {
        id
        seedJson
        createdAt
      }
    }
  }
`;

export const PROVIDER_RESPOND_INVITE = gql`
  mutation ProviderRespondInvite(
    $inviteId: ID!
    $action: InviteStatus!
    $providerNote: String
    $agreementAccepted: Boolean
  ) {
    providerRespondInvite(
      inviteId: $inviteId
      action: $action
      providerNote: $providerNote
      agreementAccepted: $agreementAccepted
    ) {
      id
      status
      respondedAt
      providerNote
      group {
        id
        seedJson
        createdAt
      }
      business {
        id
      }
    }
  }
`;

export const MY_BROADCAST_INVITES_FOR_REFERRER = gql`
  query MyBroadcastInvites($statuses: [InviteStatus!]) {
    myReferralBroadcastInvites(statuses: $statuses) {
      id
      status
      respondedAt
      providerNote
      business {
        id
        name
      }
      group {
        id
        seedJson
        createdAt
        winnerInviteId
      }
    }
  }
`;

export const PICK_BROADCAST_WINNER = gql`
  mutation PickBroadcastWinner($groupId: ID!, $inviteId: ID!) {
    pickBroadcastWinner(groupId: $groupId, inviteId: $inviteId) {
      id
      winnerInviteId
      updatedAt
      __typename
    }
  }
`;

const REFERRAL_BROADCAST_GROUP_FRAGMENT = gql`
  fragment ReferralBroadcastGroupFragment on ReferralBroadcastGroup {
    id
    winnerInviteId
    updatedAt
    __typename
  }
`;

/** =======================================================
 *  Status helpers
 *  ===================================================== */
const ALL_STATUSES = [
  "DRAFT",
  "SENT",
  "NO_RESPONSE",
  "ACCEPTED",
  "DECLINED",
  "EXPIRED",
] as InviteStatus[];

const STATUS_LABEL: Record<InviteStatus, string> = {
  DRAFT: "Agent Review",
  SENT: "Pending",
  NO_RESPONSE: "No Response",
  ACCEPTED: "Accepted",
  DECLINED: "Declined",
  EXPIRED: "Expired",
};

const BUNDLE_PENDING: InviteStatus[] = ["DRAFT", "SENT", "NO_RESPONSE"];

function normalizeStatus(
  input: string | null | undefined
): InviteStatus | "UNKNOWN" {
  if (!input) return "UNKNOWN";
  const raw = String(input).toUpperCase().trim();
  if (raw === "ACCEPT" || raw === "ACCEPTING" || raw === "ACCEPTED")
    return "ACCEPTED";
  if (raw === "DECLINE" || raw === "DECLINED") return "DECLINED";
  if (raw === "DRAFT") return "DRAFT";
  if (raw === "NO_RESPONSE" || raw === "NO-RESPONSE" || raw === "NO RESPONSE")
    return "NO_RESPONSE";
  if (raw === "SENT") return "SENT";
  if (raw === "EXPIRED") return "EXPIRED";
  return "UNKNOWN";
}

function isAccepted(s: string | null | undefined): boolean {
  return normalizeStatus(s) === "ACCEPTED";
}
function isPending(s: string | null | undefined): boolean {
  const n = normalizeStatus(s);
  return n === "SENT" || n === "NO_RESPONSE";
}
function isPickable(s: string | null | undefined): boolean {
  return normalizeStatus(s) === "ACCEPTED";
}

function statusMeta(
  status: string | null | undefined
): { color: "default" | "success" | "warning" | "error"; label: string } {
  const n = normalizeStatus(status);
  switch (n) {
    case "DRAFT":
      return { color: "default", label: STATUS_LABEL.DRAFT };
    case "ACCEPTED":
      return { color: "success", label: STATUS_LABEL.ACCEPTED };
    case "DECLINED":
      return { color: "error", label: STATUS_LABEL.DECLINED };
    case "NO_RESPONSE":
      return { color: "warning", label: STATUS_LABEL.NO_RESPONSE };
    case "SENT":
      return { color: "default", label: STATUS_LABEL.SENT };
    case "EXPIRED":
      return { color: "default", label: STATUS_LABEL.EXPIRED };
    default:
      return { color: "default", label: "Unknown" };
  }
}

/** =======================================================
 *  Seed parsing (safe)
 *  ===================================================== */
function parseSeed(seedJson: unknown): {
  residentInitials?: string | null;
  careNeed?: string | null;
  payerType?: string | null;
  notes?: string | null;
  urgency?: string | null;
  referrerContact?: { name?: string; email?: string; phone?: string } | null;
} {
  try {
    const v = typeof seedJson === "string" ? JSON.parse(seedJson) : seedJson;
    if (!v || typeof v !== "object") return {};
    const anyV = v as Record<string, unknown>;
    const rawExtra = anyV.extra;
    const extra =
      rawExtra && typeof rawExtra === "string"
        ? JSON.parse(rawExtra)
        : (rawExtra as Record<string, unknown> | undefined) || {};

    return {
      residentInitials:
        (anyV.residentKey as string) ||
        (anyV.residentInitials as string) ||
        (extra.patientInitials as string) ||
        null,
      careNeed:
        (anyV.careLevelRequested as string) ||
        (anyV.careNeed as string) ||
        (extra.careNeed as string) ||
        null,
      payerType:
        (anyV.payerType as string) || (extra.payerType as string) || null,
      notes:
        (anyV.intakeNotes as string) ||
        (anyV.notes as string) ||
        (extra.notes as string) ||
        null,
      urgency: (anyV.urgency as string) || null,
      referrerContact: (extra.referrerContact as
        | { name?: string; email?: string; phone?: string }
        | null) as any,
    };
  } catch {
    return {};
  }
}

function formatDateTimeLabel(value?: string | null) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d
    .toLocaleString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    })
    .replace(/\s?(AM|PM)\b/g, (m) => m.toLowerCase());
}

/** =======================================================
 *  Small dialog (typed)
 *  ===================================================== */
function RespondDialog(props: {
  open: boolean;
  onClose: () => void;
  onSubmit: (note: string, agreementAccepted: boolean) => void;
  title: string;
  actionLabel: string;
  initialNote?: string;
  requiresAgreement?: boolean;
  agreementUrl?: string | null;
}) {
  const {
    open,
    onClose,
    onSubmit,
    title,
    actionLabel,
    initialNote = "",
    requiresAgreement = false,
    agreementUrl = null,
  } = props;
  const [note, setNote] = React.useState<string>(initialNote);
  const [agreementAccepted, setAgreementAccepted] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setNote(initialNote || "");
      setAgreementAccepted(false);
    }
  }, [open, initialNote]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {requiresAgreement ? (
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Review this request and the Plany365 Care Provider Service Agreement for this case
            before final acceptance.
            {agreementUrl ? (
              <Button
                component={RouterLink}
                to={agreementUrl}
                target="_blank"
                rel="noreferrer"
                size="small"
                sx={{ ml: 1 }}
              >
                View full agreement
              </Button>
            ) : null}
          </Alert>
        ) : null}
        <TextField
          label="Optional note (shown to referrer)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          placeholder="Add availability details, requirements, or timing."
          sx={{ mt: 1 }}
        />
        {requiresAgreement ? (
          <Box sx={{ mt: 2 }}>
            <label>
              <input
                type="checkbox"
                checked={agreementAccepted}
                onChange={(e) => setAgreementAccepted(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              I have reviewed and agree to both this request and the Plany365 Care Provider
              Service Agreement.
            </label>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onSubmit(note, agreementAccepted)}
          disabled={requiresAgreement && !agreementAccepted}
        >
          {actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** =======================================================
 *  StatusMultiFilter (typed)
 *  ===================================================== */
function StatusMultiFilter(props: {
  value: InviteStatus[];
  onChange: (next: InviteStatus[]) => void;
  onApply?: (next: InviteStatus[]) => void;
  label?: string;
  size?: "small" | "medium";
}) {
  const { value, onChange, onApply, label = "Statuses", size = "small" } =
    props;

  const renderValue = (selected: readonly string[]) => {
    const sel = new Set(selected as InviteStatus[]);
    const parts: string[] = [];
    const hasDraft = sel.has("DRAFT");
    const hasSent = sel.has("SENT");
    const hasNR = sel.has("NO_RESPONSE");
    const used = new Set<InviteStatus>();
    if (hasDraft && hasSent && hasNR) {
      parts.push("Pending");
      used.add("DRAFT");
      used.add("SENT");
      used.add("NO_RESPONSE");
    } else if (hasSent && hasNR) {
      parts.push("Pending");
      used.add("SENT");
      used.add("NO_RESPONSE");
    }
    (["ACCEPTED", "DECLINED", "EXPIRED", "DRAFT", "SENT", "NO_RESPONSE"] as InviteStatus[])
      .filter((s) => !used.has(s) && sel.has(s))
      .forEach((s) => parts.push(STATUS_LABEL[s]));
    return parts.length ? parts.join(", ") : "None";
  };

  const apply = (next: InviteStatus[]) => {
    onChange(next);
    onApply?.(next);
  };

  const handleSelectChange = (e: SelectChangeEvent<InviteStatus[]>) => {
    const next = (e.target.value as InviteStatus[]) ?? [];
    apply(next);
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <FormControl size={size} sx={{ minWidth: 260 }}>
        <InputLabel id="status-multi">{label}</InputLabel>
        <Select<InviteStatus[]>
          labelId="status-multi"
          label={label}
          multiple
          value={value}
          renderValue={renderValue}
          onChange={handleSelectChange}
          MenuProps={{ PaperProps: { style: { maxHeight: 480 } } }}
        >
          <MenuItem disabled>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              Pending bundle
            </Typography>
          </MenuItem>
          {(["DRAFT", "SENT", "NO_RESPONSE"] as InviteStatus[]).map((s) => (
            <MenuItem key={s} value={s}>
              <Checkbox checked={value.includes(s)} />
              <ListItemText primary={STATUS_LABEL[s]} />
            </MenuItem>
          ))}
          <Divider sx={{ my: 0.5 }} />
          {(["ACCEPTED", "DECLINED", "EXPIRED"] as InviteStatus[]).map((s) => (
            <MenuItem key={s} value={s}>
              <Checkbox checked={value.includes(s)} />
              <ListItemText primary={STATUS_LABEL[s]} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
}

/** =======================================================
 *  Main Component (TSX)
 *  ===================================================== */
export default function BroadcastInvitesPanel({
  role,
  businessId,
  statuses,
  title,
  panelMode = false,
  onProviderInviteResponded,
  onCountChange,
}: BroadcastInvitesPanelProps) {
  const location = useLocation();
  const isProd = process.env.NODE_ENV === "production";
  const providerPollMs = isProd ? 30000 : 15000;
  // Role-aware defaults
  const providerDefault: InviteStatus[] = [...BUNDLE_PENDING];
  const referrerDefault: InviteStatus[] = ["SENT", "NO_RESPONSE", "ACCEPTED"];
  const effectiveStatuses: InviteStatus[] =
    statuses && statuses.length
      ? statuses
      : role === "provider"
        ? providerDefault
        : referrerDefault;

  const [localStatuses, setLocalStatuses] =
    React.useState<InviteStatus[]>(effectiveStatuses);

  React.useEffect(() => {
    setLocalStatuses(effectiveStatuses);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, statuses]);

  /** -------- Provider View -------- */
  type QProvVars = {
    businessId: number | null | undefined;
    statuses: InviteStatus[];
  };
  type QProvRes = { myReferralInvites: ProviderInvite[] };

  const {
    data: provData,
    loading: provLoading,
    error: provError,
    refetch: provRefetch,
  } = useQuery<QProvRes, QProvVars>(MY_PENDING_BROADCAST_INVITES, {
    variables:
      role === "provider"
        ? { businessId: businessId ?? null, statuses: localStatuses }
        : { businessId: 0, statuses: localStatuses },
    skip: role !== "provider" || !businessId,
    fetchPolicy: "cache-and-network",
    pollInterval: role === "provider" ? providerPollMs : 0,
  });

  type MRespondVars = {
    inviteId: string;
    action: InviteStatus;
    providerNote?: string | null;
    agreementAccepted?: boolean | null;
  };
  type MRespondRes = {
    providerRespondInvite: {
      id: string;
      status: string;
      respondedAt?: string | null;
      providerNote?: string | null;
    };
  };

  const [respond, { loading: responding, error: respondError }] = useMutation<
    MRespondRes,
    MRespondVars
  >(PROVIDER_RESPOND_INVITE, {
    onCompleted: async () => {
      await provRefetch?.();
      await onProviderInviteResponded?.();
    },
  });

  const providerInvites: ProviderInvite[] =
    (provData?.myReferralInvites as ProviderInvite[]) ?? [];
  React.useEffect(() => {
    if (role === "provider") {
      onCountChange?.(providerInvites.length);
    }
  }, [onCountChange, providerInvites.length, role]);

  const [confirm, setConfirm] = React.useState<{
    inviteId: string | null;
    action: "ACCEPTED" | "DECLINED" | null;
    businessId: string | null;
  }>({
    inviteId: null,
    action: null,
    businessId: null,
  });

  const onDecline = (inviteId: string, businessId?: string | null) =>
    setConfirm({ inviteId, action: "DECLINED", businessId: businessId || null });
  const closeDialog = () => setConfirm({ inviteId: null, action: null, businessId: null });

  const doRespond = async (note: string, agreementAccepted = false) => {
    if (!confirm.inviteId || !confirm.action) return;
    await respond({
      variables: {
        inviteId: confirm.inviteId,
        action: confirm.action,
        providerNote: note || null,
        agreementAccepted: confirm.action === "ACCEPTED" ? agreementAccepted : false,
      },
    });
    closeDialog();
  };

  /** -------- Referrer View -------- */
  type QRefVars = { statuses: InviteStatus[] };
  type QRefRes = { myReferralBroadcastInvites: ReferrerGroupInvite[] };

  const {
    data: refData,
    loading: refLoading,
    error: refError,
    refetch: refRefetch,
  } = useQuery<QRefRes, QRefVars>(MY_BROADCAST_INVITES_FOR_REFERRER, {
    variables: { statuses: localStatuses },
    skip: role !== "referrer",
    fetchPolicy: "cache-and-network",
  });

  type MPickVars = { groupId: string; inviteId: string };
  type MPickRes = {
    pickBroadcastWinner: {
      __typename: "ReferralBroadcastGroup";
      id: string;
      winnerInviteId: string;
      updatedAt?: string | null;
    };
  };

  const [pickWinner, { loading: picking, error: pickError }] = useMutation<
    MPickRes,
    MPickVars
  >(PICK_BROADCAST_WINNER, {
    optimisticResponse: (vars) => ({
      pickBroadcastWinner: {
        __typename: "ReferralBroadcastGroup",
        id: vars.groupId,
        winnerInviteId: vars.inviteId,
        updatedAt: new Date().toISOString(),
      },
    }),
    update: (cache, { data }) => {
      const grp = data?.pickBroadcastWinner;
      if (!grp?.id) return;
      cache.writeFragment({
        id: cache.identify({
          __typename: "ReferralBroadcastGroup",
          id: grp.id,
        }),
        fragment: REFERRAL_BROADCAST_GROUP_FRAGMENT,
        data: {
          __typename: "ReferralBroadcastGroup",
          id: grp.id,
          winnerInviteId: grp.winnerInviteId,
          updatedAt: grp.updatedAt ?? new Date().toISOString(),
        },
      });
    },
    onCompleted: () => {
      refRefetch?.();
    },
  });

  const flatInvites: ReferrerGroupInvite[] =
    (refData?.myReferralBroadcastInvites as ReferrerGroupInvite[]) ?? [];

  // Group + filter by selected statuses + sort
  const groups: ReferrerGroup[] = React.useMemo(() => {
    const allowed = new Set(localStatuses);
    const byId = new Map<string, RefGroupWorking>();

    flatInvites.forEach((raw) => {
      const norm = normalizeStatus(raw.status);
      if (norm === "UNKNOWN") return;
      if (!allowed.has(norm)) return;

      const inv: ReferrerGroupInvite = { ...raw, status: norm };
      const gid: string = inv?.group?.id ?? "UNGROUPED";
      const createdAt: string | null = inv?.group?.createdAt ?? null;
      const seedJson: unknown = inv?.group?.seedJson;
      const winnerInviteId: string | null | undefined =
        inv?.group?.winnerInviteId ?? null;

      if (!byId.has(gid)) {
        byId.set(gid, {
          id: gid,
          createdAt,
          seedJson,
          winnerInviteId: winnerInviteId ?? null,
          invites: [],
          _rawInvites: [],
        });
      } else {
        const g0 = byId.get(gid)!;
        if (!g0.winnerInviteId && winnerInviteId)
          g0.winnerInviteId = winnerInviteId;
      }
      byId.get(gid)!._rawInvites.push(inv);
    });

    const out: ReferrerGroup[] = [];
    byId.forEach((g) => {
      const filtered = g._rawInvites.slice();
      filtered.sort((a, b) => {
        const aAcc = isAccepted(a.status) ? 1 : 0;
        const bAcc = isAccepted(b.status) ? 1 : 0;
        if (aAcc !== bAcc) return bAcc - aAcc;
        const ta = a.respondedAt ? Date.parse(a.respondedAt) : 0;
        const tb = b.respondedAt ? Date.parse(b.respondedAt) : 0;
        return tb - ta;
      });

      out.push({
        id: g.id,
        createdAt: g.createdAt,
        seedJson: g.seedJson,
        winnerInviteId: g.winnerInviteId,
        invites: filtered,
      });
    });

    out.sort((a, b) => {
      const ta = a.createdAt ? Date.parse(a.createdAt) : 0;
      const tb = b.createdAt ? Date.parse(b.createdAt) : 0;
      return tb - ta;
    });

    return out;
  }, [flatInvites, localStatuses]);

  const loading = role === "provider" ? provLoading : refLoading;
  const error: ApolloError | undefined =
    role === "provider" ? provError : refError;

  const headerTitle =
    title ?? (role === "provider" ? "Broadcast Invites" : "Your Broadcasts");

  const totalVisibleCount =
    role === "provider"
      ? (provData?.myReferralInvites?.length ?? 0)
      : groups.reduce((acc, g) => acc + (g.invites?.length || 0), 0);

  return (
    <Accordion
      expanded={panelMode ? true : undefined}
      onChange={panelMode ? () => { } : undefined}
      disableGutters={panelMode}
      sx={{
        mb: { xs: 1.5, sm: 2 },
        ...(panelMode
          ? {
            boxShadow: "none",
            border: 0,
            "&:before": { display: "none" },
          }
          : {}),
      }}
    >
      {/* Keep summary simple: title + count only (no interactive controls) */}
      <AccordionSummary expandIcon={panelMode ? <></> : <ExpandMoreIcon />}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ width: "100%" }}
        >
          <Typography variant="h6">{headerTitle}</Typography>
          <Chip
            size="small"
            label={totalVisibleCount}
            variant="outlined"
            sx={{ ml: { sm: 0.5 } }}
          />
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={panelMode ? { px: 0, pb: 0, pt: 0 } : undefined}>
        {/* Toolbar row for filters: lives in Details so it never toggles the accordion */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", md: "center" }}
          sx={{ mb: 1 }}
        >
          <Box sx={{ flex: 1 }} />
          {/* Status filter (shared for both roles) */}
          <StatusMultiFilter
            value={localStatuses}
            onChange={setLocalStatuses}
            onApply={(next) => {
              if (role === "provider") {
                provRefetch?.({
                  businessId: businessId ?? null,
                  statuses: next,
                });
              } else {
                refRefetch?.({ statuses: next });
              }
            }}
          />
        </Stack>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {String(error.message)}
          </Alert>
        )}
        {respondError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {String(respondError.message)}
          </Alert>
        )}
        {pickError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {String(pickError.message)}
          </Alert>
        )}

        {/* -------- Provider list -------- */}
        {role === "provider" && (
          <>
            {providerInvites.length > 0 ? (
              <Stack spacing={1.5}>
                {providerInvites.map((inv) => {
                  const seed = parseSeed(inv.group?.seedJson);
                  const norm = normalizeStatus(inv.status);
                  const canRespond = isPending(norm); // enable actions only when pending
                  return (
                    <Paper
                      key={inv.id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: (t) => alpha(t.palette.info.main, 0.04),
                        borderColor: (t) => alpha(t.palette.info.main, 0.25),
                      }}
                    >
                      <Stack spacing={1}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{ mr: 1 }}
                          >
                            Pre-lead Invite
                          </Typography>
                          <Chip
                            size="small"
                            label={statusMeta(norm).label}
                            color={statusMeta(norm).color}
                          />
                          <Box sx={{ ml: "auto" }} />
                          {/* Provider dashboard: no group detail link */}
                        </Stack>

                        {/* Seed details */}
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {seed.residentInitials && (
                            <Chip
                              size="small"
                              label={`Resident: ${seed.residentInitials}`}
                            />
                          )}
                          {seed.careNeed && (
                            <Chip size="small" label={`Care: ${seed.careNeed}`} />
                          )}
                          {seed.payerType && (
                            <Chip size="small" label={`Payer: ${seed.payerType}`} />
                          )}
                          {seed.urgency && (
                            <Chip size="small" label={`Urgency: ${seed.urgency}`} />
                          )}
                        </Stack>

                        {seed.referrerContact?.name ||
                          seed.referrerContact?.email ||
                          seed.referrerContact?.phone ? (
                          <>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              Referrer: {seed.referrerContact?.name || "—"}
                              {seed.referrerContact?.email
                                ? ` • ${seed.referrerContact.email}`
                                : ""}
                              {seed.referrerContact?.phone
                                ? ` • ${seed.referrerContact.phone}`
                                : ""}
                            </Typography>
                          </>
                        ) : null}

                        {seed.notes && (
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap" }}
                          >
                            <strong>Notes: </strong>
                            {seed.notes}
                          </Typography>
                        )}

                        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<CheckCircleIcon />}
                            component={seed.payerType === "PRIVATE" ? RouterLink : "button"}
                            to={
                              seed.payerType === "PRIVATE"
                                ? `/care/agreement?${new URLSearchParams({
                                    invite: String(inv.id),
                                    businessId: String(inv.business?.id || ""),
                                    returnTo: location.pathname + location.search,
                                  }).toString()}`
                                : undefined
                            }
                            onClick={
                              seed.payerType === "PRIVATE"
                                ? undefined
                                : () =>
                                    respond({
                                      variables: {
                                        inviteId: String(inv.id),
                                        action: "ACCEPTED",
                                        agreementAccepted: false,
                                      },
                                    })
                            }
                            disabled={responding || !canRespond}
                          >
                            {seed.payerType === "PRIVATE"
                              ? "Review Request and Agreement"
                              : "Accept Request"}
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            color="error"
                            startIcon={<CancelIcon />}
                            onClick={() => onDecline(inv.id, inv.business?.id || null)}
                            disabled={responding || !canRespond}
                          >
                            Decline
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            ) : (
              !loading && (
                <Card variant="outlined" sx={{ p: 3, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "1rem" }}
                  >
                    No broadcast invites right now.
                  </Typography>
                </Card>
              )
            )}

            <RespondDialog
              open={!!(confirm.inviteId && confirm.action)}
              onClose={closeDialog}
              onSubmit={doRespond}
              title={
                confirm.action === "ACCEPTED"
                  ? "Review Request and Agreement"
                  : "Decline Invite"
              }
              actionLabel={
                confirm.action === "ACCEPTED" ? "Accept After Review" : "Decline"
              }
              requiresAgreement={confirm.action === "ACCEPTED"}
              agreementUrl={
                confirm.businessId
                  ? `/care/agreement?businessId=${encodeURIComponent(confirm.businessId)}`
                  : null
              }
            />
          </>
        )}

        {/* -------- Referrer list -------- */}
        {role === "referrer" && (
          <>
            {groups.length > 0 ? (
              <Stack spacing={1.5}>
                {groups.map((g) => {
                  const seed = parseSeed(g.seedJson);
                  const acceptedCount = g.invites.filter((i) =>
                    isAccepted(i.status)
                  ).length;
                  const pendingCount = g.invites.filter((i) =>
                    isPending(i.status)
                  ).length;
                  const declinedCount = g.invites.filter(
                    (i) => normalizeStatus(i.status) === "DECLINED"
                  ).length;
                  const expiredCount = g.invites.filter(
                    (i) => normalizeStatus(i.status) === "EXPIRED"
                  ).length;
                  const inviteCount = g.invites?.length || 0;

                  return (
                    <Paper
                      key={g.id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: (t) => alpha(t.palette.success.main, 0.035),
                        borderColor: (t) => alpha(t.palette.success.main, 0.25),
                      }}
                    >
                      <Stack spacing={1.25}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{ mr: 1 }}
                          >
                            Broadcast Group
                          </Typography>
                          <Chip
                            size="small"
                            color="success"
                            label={`${acceptedCount} accepted`}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${pendingCount} pending`}
                          />
                          <Chip
                            size="small"
                            color="error"
                            variant="outlined"
                            label={`${declinedCount} declined`}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${expiredCount} expired`}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${inviteCount} total`}
                          />
                          <Box sx={{ ml: "auto" }} />
                          <Tooltip title="View full group details">
                            <Button
                              size="small"
                              startIcon={<VisibilityIcon />}
                              component={RouterLink}
                              to={`/care/referrals/group/${g.id}`}
                            >
                              View Group
                            </Button>
                          </Tooltip>
                        </Stack>

                        {/* Seed details */}
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {seed.residentInitials && (
                            <Chip
                              size="small"
                              label={`Resident: ${seed.residentInitials}`}
                            />
                          )}
                          {seed.careNeed && (
                            <Chip
                              size="small"
                              label={`Care: ${seed.careNeed}`}
                            />
                          )}
                          {seed.payerType && (
                            <Chip
                              size="small"
                              label={`Payer: ${seed.payerType}`}
                            />
                          )}
                          {seed.urgency && (
                            <Chip
                              size="small"
                              label={`Urgency: ${seed.urgency}`}
                            />
                          )}
                        </Stack>

                        {seed.notes && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ whiteSpace: "pre-wrap" }}
                          >
                            <strong>Notes: </strong>
                            {seed.notes}
                          </Typography>
                        )}

                        {/* Child invites */}
                        <Stack spacing={1} sx={{ mt: 0.5 }}>
                          {g.invites.map((inv) => {
                            const meta = statusMeta(inv.status);
                            const winnerId = g.winnerInviteId ?? null;
                            const isWinner = winnerId === inv.id;
                            const winnerAlreadyChosen = !!winnerId && !isWinner;

                            return (
                              <Paper
                                key={inv.id}
                                variant="outlined"
                                sx={{
                                  p: 1.25,
                                  borderRadius: 1.5,
                                  bgcolor: (t) => alpha(t.palette.grey[500], 0.04),
                                  borderColor: (t) => alpha(t.palette.grey[500], 0.25),
                                  borderLeft: (t) =>
                                    isWinner
                                      ? `3px solid ${t.palette.info.main}`
                                      : isAccepted(inv.status)
                                        ? `3px solid ${t.palette.success.main}`
                                        : undefined,
                                }}
                              >
                                <Stack
                                  direction={{ xs: "column", sm: "row" }}
                                  spacing={1}
                                  alignItems={{ sm: "center" }}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {inv.business?.name || "Provider"}
                                  </Typography>

                                  <Chip
                                    size="small"
                                    label={meta.label}
                                    color={meta.color}
                                    sx={{ ml: { sm: 1 } }}
                                  />
                                  {isWinner && (
                                    <Chip size="small" color="info" label="Picked" sx={{ ml: 1 }} />
                                  )}

                                  <Box sx={{ flex: 1 }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {inv.respondedAt
                                      ? `Responded: ${formatDateTimeLabel(inv.respondedAt) || "—"}`
                                      : "No provider has responded yet"}
                                  </Typography>
                                </Stack>

                                {inv.providerNote && (
                                  <Typography
                                    variant="body2"
                                    sx={{ mt: 0.75, whiteSpace: "pre-wrap" }}
                                  >
                                    <strong>Note: </strong>
                                    {inv.providerNote}
                                  </Typography>
                                )}

                                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                  <Tooltip
                                    title={
                                      isWinner
                                        ? "Already picked"
                                        : winnerAlreadyChosen
                                          ? isPickable(inv.status)
                                            ? "Pick this provider instead"
                                            : "You can only pick an ACCEPTED provider"
                                          : isPickable(inv.status)
                                            ? "Pick this provider"
                                            : "You can only pick an ACCEPTED provider"
                                    }
                                  >
                                    <span>
                                      <Button
                                        size="small"
                                        variant={isWinner ? "outlined" : "contained"}
                                        disabled={
                                          picking ||
                                          isWinner ||
                                          !isPickable(inv.status)
                                        }
                                        onClick={() =>
                                          pickWinner({
                                            variables: { groupId: g.id, inviteId: inv.id },
                                          })
                                        }
                                      >
                                        {isWinner
                                          ? "Picked"
                                          : winnerAlreadyChosen
                                            ? "Pick Instead"
                                            : "Pick Provider"}
                                      </Button>
                                    </span>
                                  </Tooltip>
                                </Stack>
                              </Paper>
                            );
                          })}
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            ) : (
              !loading && (
                <Card variant="outlined" sx={{ p: 3, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "1rem" }}
                  >
                    You don’t have any broadcast groups yet.
                  </Typography>
                </Card>
              )
            )}
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
